// src/components/NewsBox.jsx
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { FaShareAlt, FaCheckCircle } from "react-icons/fa";
import styles from "../css/NewsBox.module.css";

export default function NewsBox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readItems, setReadItems] = useState({});
  const containerRef = useRef(null);

  // Load “read” status from localStorage
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("readItems") || "{}");
    setReadItems(stored);
  }, []);

  // Fetch news items (no quizzes)
  useEffect(() => {
    fetch("https://fetchrss.visiontrek.io/api/items")
      .then((res) => res.json())
      .then((data) =>
        // preload images or skip broken ones
        Promise.all(
          data.map(
            (item) =>
              new Promise((resolve) => {
                const img = new Image();
                const src =
                  item.enclosure?.[1]?.href ||
                  item.enclosure?.[0]?.href ||
                  ((item.summary || "")
                    .replace(/<a[^>]+><\/a>/gi, "")
                    .match(/<img[^>]+src=["']([^"']+)["']/i) || [])[1] ||
                  "";
                if (!src) return resolve(item);
                img.onload = () => resolve(item);
                img.onerror = () => resolve(null);
                img.src = src;
              })
          )
        ).then((arr) => {
          setItems(arr.filter(Boolean));
        })
      )
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load news feed");
      })
      .finally(() => setLoading(false));
  }, []);

  // Mark items “read” after 10s in view
  useEffect(() => {
    if (!items.length) return;
    const timers = {};
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.dataset.id;
          if (!id) return;

          if (entry.isIntersecting && !timers[id]) {
            timers[id] = setTimeout(() => {
              setReadItems((prev) => {
                if (prev[id]) return prev;
                const next = { ...prev, [id]: true };
                localStorage.setItem("readItems", JSON.stringify(next));
                return next;
              });
            }, 10000);
          } else if (!entry.isIntersecting && timers[id]) {
            clearTimeout(timers[id]);
            delete timers[id];
          }
        });
      },
      { root: containerRef.current, threshold: 0.6 }
    );

    containerRef.current
      .querySelectorAll(".card[data-id]")
      .forEach((c) => observer.observe(c));

    return () => {
      observer.disconnect();
      Object.values(timers).forEach(clearTimeout);
    };
  }, [items]);

  const handleShare = (id) => {
    const url = `${window.location.origin}${window.location.pathname}?id=${id}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("🔗 Link copied!"))
      .catch(() => toast.error("❌ Copy failed"));
  };

  if (loading) return <div className={styles.loading}>Loading news…</div>;
  if (!items.length)
    return <div className={styles.loading}>No news to display.</div>;

  return (
    <div className={styles.newsBox}>
      <h2 className={styles.title}>Latest News</h2>
      <div
        className={styles.feedContainer}
        ref={containerRef}
        onScroll={() => {}}
      >
        {items.map((item) => {
          // derive summary text
          const summaryText = (item.summary || "")
              .replace(/<[^>]+>/g, "")   // removes ALL HTML tags
  .replace(/\s+/g, " ")
  .trim();

          const imageUrl =
            item.enclosure?.[1]?.href || item.enclosure?.[0]?.href || "";

          const read = !!readItems[item.id];

          return (
            <div
              key={item.id}
              className={styles.card}
              data-id={item.id}
              style={{ scrollMarginTop: "80px" }}
            >
              {imageUrl && (
                <img className={styles.cardImage} src={imageUrl} alt="" />
              )}
              <div className={styles.cardContent}>
                <h2 className={styles.cardTitle}>
                  {(item.title || "").replace(/&pound;/gi, "$")}
                </h2>
                <div className={styles.cardMeta}>
                  {new Date(item.publishedAt).toLocaleString()}
                  {item.author ? ` • ${item.author}` : ""}
                </div>

                <div className={styles.cardSummary}>
                  <div className="summary-text">{summaryText}</div>

                  {read && (
                    <div className={styles.readSection}>
                      <FaCheckCircle
                        className={`${styles.readIcon} ${styles.read}`}
                      />
                      <span className={styles.readLabel}>Recently Read</span>
                    </div>
                  )}

                  {/* <div className={styles.shareSection}>
                    <button
                      className={styles.shareBtn}
                      onClick={() => handleShare(item.id)}
                      title="Share this news"
                    >
                      <FaShareAlt />
                    </button>
                    <span className={styles.shareLabel}>Share</span>
                  </div> */}

                  <a
                    className={styles.cardLink}
                    href={item.canonical?.[0]?.href || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginTop: "10px" }}
                  >
                    Read more →
                  </a>
                </div>
              </div>
            </div>
          );
        })}

        <div className={styles.endCard}>
          <div className="card-content">
            <h2>No more news</h2>
          </div>
        </div>
      </div>
    </div>
  );
}
